import * as React from 'react';
import { converseProcedure } from '@krishanmadan/core/src/procedures/converseProcedure';
import { uuidv4 } from '@epihub/core/src/fn/uuidv4';
import { ASSISTANT_NAME } from '@krishanmadan/core/src/model/const';
import type { ActionEvent } from '@krishanmadan/core/src/model/actions';
import {
  ChatViewMessageBoxAnimationContext,
  type TAbsoluteOrigin,
} from '../components/chat/ChatViewMessageBoxAnimationContext';
import { useMutationProcedure } from '../_ctx/queryClient';
import { useGlobalStore } from '../store/useGlobalStore';
import { handleActionEvent } from './useActionListener';

const audio = new Audio('./send.mp3');

export function useSendMessage() {
  const { chatViewMessageBoxAnimationContextRef } = React.useContext(ChatViewMessageBoxAnimationContext);

  const language = useGlobalStore((store) => store.settings.language);
  const converse = useMutationProcedure(converseProcedure, {
    request: { body: { language } },
  });
  return React.useCallback(async (text: string, animationOrigin?: TAbsoluteOrigin) => {
    const visitorMessageId = uuidv4();

    // Before adding visitors message set that it will animate, this prevents
    // the flicker caused by starting the animation after having added the message
    chatViewMessageBoxAnimationContextRef.current.setAnimateMessageById(visitorMessageId, animationOrigin);

    if (useGlobalStore.getState().settings.soundOn) {
      audio.play();
    }

    // Add users message and initial system message
    const responseId = uuidv4();
    useGlobalStore.getState().conversation.updateConversation((prev) => {
      return {
        ...prev,
        messages: [
          ...prev.messages,
          {
            id: visitorMessageId,
            content: text,
            createdAt: new Date().getTime(),
            role: 'user',
          },
          {
            id: responseId,
            content: '',
            createdAt: new Date().getTime(),
            role: 'assistant',
            user: ASSISTANT_NAME,
          },
        ],
      };
    });

    const response = await converse.mutateAsync({
      request: {
        body: {
          ...useGlobalStore.getState().conversation.conversation,
          responseId,
        },
      },
    });

    useGlobalStore.getState().conversation.updateConversation(() => ({ messages: response.messages }));
    // Playback events, deduplication is handled inside
    for (const item of response.actionEvents) {
      handleActionEvent(item as ActionEvent);
    }
  }, [chatViewMessageBoxAnimationContextRef]);
}
